import './SequenceCell.scss';

import type { Location } from 'history';
import { bem } from 'mission-control-common-components';
import React, { memo } from 'react';

import EditableCell from '../../../../../common/EditableTableCells/EditableCell';
import useNavigationWithLocalStorage from '../../../../../common/hooks/useNavigationWithLocalStorage';
import Tooltip from '../../../../../common/Tooltip';
import { PathName } from '../../../../../constants';
import { useFeatureFlags } from '../../../../../hooks/useFeatureFlags';
import type { SequenceLocationState } from '../../../../Sequence/SequencePage';

export type SequenceCellProps = {
  parentLineOmsLinkId?: string;
  lineItemId?: string;
  title?: string | number;
};

const [block, element] = bem('sequence-cell');

export const SEQUENCE_CELL_TOOLTIP_MESSAGE = 'Edit in MC Sequencing pages';

const SequenceCell = ({ parentLineOmsLinkId, lineItemId, title }: SequenceCellProps): React.JSX.Element => {
  const { adsLiveDaiStandaloneLineSequences } = useFeatureFlags();
  const { navigateWithLocalStorage } = useNavigationWithLocalStorage();

  const isClickable = !!parentLineOmsLinkId || (adsLiveDaiStandaloneLineSequences && !!lineItemId);

  const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    if (!isClickable) {
      return;
    }

    const locationState: SequenceLocationState = parentLineOmsLinkId
      ? { uniqueParentLineOmsLinkIds: [parentLineOmsLinkId] }
      : { uniqueParentLineOmsLinkIds: [], lineItemIdList: [lineItemId as string] };

    const location: Location = {
      pathname: PathName.sequence,
      state: {
        locationState,
      },
      key: '',
      search: '',
      hash: '',
    };

    navigateWithLocalStorage(e, location, locationState);
  };

  return (
    <div className={block()} onClick={handleClick}>
      <Tooltip
        delayShow={700}
        delayHide={0}
        id={'sequence-cell-tooltip'}
        message={SEQUENCE_CELL_TOOLTIP_MESSAGE}
        disable={!isClickable}
      >
        <EditableCell variant={'base'} tabIndex={0} usePencil={isClickable}>
          <div data-testid="sequence-cell-title" className={element('title')}>
            {title ?? ''}
          </div>
        </EditableCell>
      </Tooltip>
    </div>
  );
};

export default memo(SequenceCell);
